import React, { useState, useEffect } from 'react';
import { Card, CardBody, Col, Container, Row, Button, Alert, Spinner, Badge } from 'reactstrap';
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { Link } from "react-router-dom";
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { cognitoService } from '../../services/cognitoService';
import { oauthService } from '../../services/oauthService';
import { useCognito } from '../../hooks/useCognito';

import { layoutModeTypes } from "../../Components/constants/layout";
import logoDark from '../../assets/images/exiaLogo_dark.png';
import logoLight from '../../assets/images/exiaLogo_light.png';

const CognitoTest = () => {
    const [cognitoStatus, setCognitoStatus] = useState(null);
    const [oauthStatus, setOauthStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [testResults, setTestResults] = useState({});

    const { cognitoStatus: hookStatus, isEnabled, isOAuthEnabled, loading: hookLoading } = useCognito();

    // Layout state selector
    const selectLayoutState = (state) => state.Layout;
    const layoutData = createSelector(
        selectLayoutState,
        (layout) => ({
            layoutModeType: layout.layoutModeType,
        })
    );
    const { layoutModeType } = useSelector(layoutData);

    useEffect(() => {
        testApiConnectivity();
    }, []);

    const testApiConnectivity = async () => {
        setLoading(true);
        setError('');
        const results = {};

        try {
            // Test basic backend connectivity
            console.log('Testing basic backend connectivity...');
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/health`);
                results.backendHealth = { 
                    success: response.ok, 
                    status: response.status,
                    data: response.ok ? 'Backend is accessible' : `HTTP ${response.status}`
                };
            } catch (err) {
                console.error('Backend health test failed:', err);
                results.backendHealth = { success: false, error: err.message };
            }

            // Test Cognito service status
            console.log('Testing Cognito service status...');
            try {
                const cognito = await cognitoService.getStatus();
                setCognitoStatus(cognito);
                results.cognitoStatus = { success: true, data: cognito };
            } catch (err) {
                console.error('Cognito status test failed:', err);
                results.cognitoStatus = { 
                    success: false, 
                    error: err.error || err.message || 'Failed to get Cognito status',
                    status: err.status,
                    response: err.response?.data
                };
            }

            // Test OAuth service status
            console.log('Testing OAuth service status...');
            try {
                const oauth = await oauthService.getStatus();
                setOauthStatus(oauth);
                results.oauthStatus = { success: true, data: oauth };
            } catch (err) {
                console.error('OAuth status test failed:', err);
                results.oauthStatus = { 
                    success: false, 
                    error: err.error || err.message || 'Failed to get OAuth status',
                    status: err.status,
                    response: err.response?.data
                };
            }

            // Test OAuth user info endpoint (for updated backend)
            console.log('Testing OAuth user info endpoint...');
            try {
                const userInfo = await oauthService.getUserInfo();
                results.oauthUserInfo = { success: true, data: userInfo };
            } catch (err) {
                console.error('OAuth user info test failed:', err);
                results.oauthUserInfo = { 
                    success: false, 
                    error: err.error || err.message || 'Failed to get OAuth user info',
                    status: err.status,
                    response: err.response?.data
                };
            }

            setTestResults(results);
        } catch (error) {
            console.error('API test failed:', error);
            setError('Failed to test API connectivity: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const mode = layoutModeType || layoutModeTypes.LIGHTMODE;

    document.title = "Cognito API Test | SeedNations";

    return (
        <React.Fragment>
            <ParticlesAuth>
                <div className="auth-page-content">
                    <Container>
                        <Row>
                            <Col lg={12}>
                                <div className="text-center mt-sm-5 mb-4 text-white-50">
                                    <div>
                                        <Link to="/" className="d-inline-block auth-logo">
                                            <img src={mode === layoutModeTypes.LIGHTMODE ? logoLight : logoDark} alt="" height="200" />
                                        </Link>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <Row className="justify-content-center">
                            <Col md={10} lg={8}>
                                <Card className="mt-4">
                                    <CardBody className="p-4">
                                        <div className="text-center mb-4">
                                            <h4 className="text-primary">🧪 AWS Cognito Integration Test</h4>
                                            <p className="text-muted">Test API connectivity and service status</p>
                                        </div>

                                        {error && (
                                            <Alert color="danger">
                                                <strong>Error:</strong> {error}
                                            </Alert>
                                        )}

                                        <div className="text-center mb-4">
                                            <Button 
                                                color="primary" 
                                                onClick={testApiConnectivity} 
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Testing APIs...
                                                    </>
                                                ) : (
                                                    <>🔄 Test API Connectivity</>
                                                )}
                                            </Button>
                                        </div>

                                        {/* Hook Status */}
                                        <Card className="mb-3">
                                            <CardBody>
                                                <h6 className="text-primary mb-3">📡 React Hook Status</h6>
                                                <Row>
                                                    <Col md={6}>
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span>Hook Loading:</span>
                                                            <Badge color={hookLoading ? "warning" : "success"}>
                                                                {hookLoading ? "Loading..." : "Ready"}
                                                            </Badge>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span>Cognito Enabled:</span>
                                                            <Badge color={isEnabled ? "success" : "danger"}>
                                                                {isEnabled ? "✅ Yes" : "❌ No"}
                                                            </Badge>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <span>OAuth Enabled:</span>
                                                            <Badge color={isOAuthEnabled ? "success" : "warning"}>
                                                                {isOAuthEnabled ? "✅ Yes" : "⚠️ No"}
                                                            </Badge>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        {hookStatus && (
                                                            <div className="bg-light p-2 rounded">
                                                                <small className="text-muted">
                                                                    <pre style={{ fontSize: '0.75rem', margin: 0 }}>
                                                                        {JSON.stringify(hookStatus, null, 2)}
                                                                    </pre>
                                                                </small>
                                                            </div>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>

                                        {/* API Test Results */}
                                        {Object.keys(testResults).length > 0 && (
                                            <>
                                                <h6 className="text-primary mb-3">🔍 API Test Results</h6>

                                                {/* Cognito Service Status */}
                                                <Card className="mb-3">
                                                    <CardBody>
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <h6 className="mb-0">🔐 Cognito Service Status</h6>
                                                            <Badge color={testResults.cognitoStatus?.success ? "success" : "danger"}>
                                                                {testResults.cognitoStatus?.success ? "✅ Success" : "❌ Failed"}
                                                            </Badge>
                                                        </div>
                                                        {testResults.cognitoStatus?.success ? (
                                                            <div className="bg-light p-3 rounded">
                                                                <pre style={{ fontSize: '0.8rem', margin: 0 }}>
                                                                    {JSON.stringify(testResults.cognitoStatus.data, null, 2)}
                                                                </pre>
                                                            </div>
                                                        ) : (
                                                            <Alert color="danger" className="mb-0">
                                                                <strong>Error:</strong> {testResults.cognitoStatus?.error}
                                                            </Alert>
                                                        )}
                                                    </CardBody>
                                                </Card>

                                                {/* OAuth Service Status */}
                                                <Card className="mb-3">
                                                    <CardBody>
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <h6 className="mb-0">🌐 OAuth Service Status</h6>
                                                            <Badge color={testResults.oauthStatus?.success ? "success" : "danger"}>
                                                                {testResults.oauthStatus?.success ? "✅ Success" : "❌ Failed"}
                                                            </Badge>
                                                        </div>
                                                        {testResults.oauthStatus?.success ? (
                                                            <div className="bg-light p-3 rounded">
                                                                <pre style={{ fontSize: '0.8rem', margin: 0 }}>
                                                                    {JSON.stringify(testResults.oauthStatus.data, null, 2)}
                                                                </pre>
                                                            </div>
                                                        ) : (
                                                            <Alert color="danger" className="mb-0">
                                                                <strong>Error:</strong> {testResults.oauthStatus?.error}
                                                            </Alert>
                                                        )}
                                                    </CardBody>
                                                </Card>

                                                {/* OAuth User Info Test */}
                                                {testResults.oauthUserInfo && (
                                                    <Card className="mb-3">
                                                        <CardBody>
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <h6 className="mb-0">👤 OAuth User Info Endpoint</h6>
                                                                <Badge color={testResults.oauthUserInfo?.success ? "success" : "danger"}>
                                                                    {testResults.oauthUserInfo?.success ? "✅ Success" : "❌ Failed"}
                                                                </Badge>
                                                            </div>
                                                            {testResults.oauthUserInfo?.success ? (
                                                                <div className="bg-light p-3 rounded">
                                                                    <pre style={{ fontSize: '0.8rem', margin: 0 }}>
                                                                        {JSON.stringify(testResults.oauthUserInfo.data, null, 2)}
                                                                    </pre>
                                                                </div>
                                                            ) : (
                                                                <Alert color="danger" className="mb-0">
                                                                    <strong>Error:</strong> {testResults.oauthUserInfo?.error}
                                                                    {testResults.oauthUserInfo?.status && (
                                                                        <><br /><strong>Status:</strong> {testResults.oauthUserInfo.status}</>
                                                                    )}
                                                                </Alert>
                                                            )}
                                                        </CardBody>
                                                    </Card>
                                                )}
                                            </>
                                        )}

                                        {/* Environment Configuration */}
                                        <Card className="mb-3">
                                            <CardBody>
                                                <h6 className="text-primary mb-3">⚙️ Environment Configuration</h6>
                                                <Row>
                                                    <Col md={6}>
                                                        <div className="mb-2">
                                                            <strong>API Base URL:</strong><br />
                                                            <code>{process.env.REACT_APP_API_URL || 'Not set'}</code>
                                                        </div>
                                                        <div className="mb-2">
                                                            <strong>Cognito Region:</strong><br />
                                                            <code>{process.env.REACT_APP_COGNITO_REGION || 'Not set'}</code>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="mb-2">
                                                            <strong>User Pool ID:</strong><br />
                                                            <code>{process.env.REACT_APP_COGNITO_USER_POOL_ID || 'Not set'}</code>
                                                        </div>
                                                        <div className="mb-2">
                                                            <strong>Client ID:</strong><br />
                                                            <code>{process.env.REACT_APP_COGNITO_CLIENT_ID || 'Not set'}</code>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>

                                        {/* Navigation Links */}
                                        <div className="text-center mt-4">
                                            <div className="d-flex gap-2 justify-content-center flex-wrap">
                                                <Link to="/login/cognito" className="btn btn-primary btn-sm">
                                                    🔐 Try Cognito Login
                                                </Link>
                                                <Link to="/login" className="btn btn-outline-primary btn-sm">
                                                    Traditional Login
                                                </Link>
                                                <Link to="/register" className="btn btn-outline-secondary btn-sm">
                                                    Register
                                                </Link>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ParticlesAuth>
        </React.Fragment>
    );
};

export default CognitoTest;